import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LeftNavigationComponent } from '@component/left-navigation/left-navigation';
import { TopNavigationComponent } from '@component/top-navigation/top-navigation';
import { AuthService } from '@service/auth/auth.service';
import { UserService } from '@service/auth/user.service';
import { DeviceService } from '@service/device/device.service';
import { ReadOnlyModeService } from '@service/read-only/read-only.service';
import { ResponsiveService } from '@service/responsive/responsive.service';
import { TranslationService } from '@service/translation/translation.service';
import { filter } from 'rxjs/operators';
import { environment } from './environments/environment';
import { UserProfile } from './interfaces/user.interface';
import { ReadOnlyBannerComponent } from '@component/read-only-banner/read-only-banner.component';

/**
 * Routes that take over the whole viewport on a handheld, hiding the top and left navigation.
 *
 * Only for screens that are *used* on a phone rather than merely surviving on one: held in
 * one hand, at arm's length, with the camera pointed at something. On those the chrome is not
 * just wasted space — the 56px header and the sidebar toggle sit exactly where the thumb
 * goes, and every pixel taken from the viewfinder is a container number that has to be
 * photographed twice.
 *
 * Matched as a path prefix so child routes and query parameters come along. Keep this list
 * short: each entry is a screen the operator cannot navigate away from using the menu, so it
 * must carry its own way out.
 */
// `/scan`, not the parent: `/operating/loading-verification` is the records page, a normal
// screen with a search bar and a table that needs the navigation like any other.
const HANDHELD_FULL_SCREEN_ROUTES = ['/operating/loading-verification/scan'];

/** Path-prefix match, ignoring the query string and fragment. */
const isFullScreenPath = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return HANDHELD_FULL_SCREEN_ROUTES.some(
    (route) => path === route || path.startsWith(`${route}/`),
  );
};

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    LeftNavigationComponent,
    TopNavigationComponent,
    MatIconModule,
    ReadOnlyBannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  protected readonly title = 'frontend';
  isLeftNavCollapsed = true;
  // Determine navigation visibility from the URL immediately so the template
  // never renders nav components on the login page. If this defaults to true,
  // Angular briefly creates LeftNavigation/TopNavigation which trigger API
  // calls through the AuthInterceptor, ultimately redirecting to Azure AD.
  // Also hide navigation when ?code= is in URL (OAuth callback in progress).
  showNavigation =
    !window.location.pathname.includes('/login') &&
    !window.location.pathname.includes('/inactive-user') &&
    !window.location.search.includes('code=');

  // User information for top navigation
  userName = '';
  userAvatar = '';
  userProfile: UserProfile | null = null;

  // Mobile navigation state
  mobileMenuOpen = signal(false);

  // Responsive service for mobile detection
  protected readonly responsiveService = inject(ResponsiveService);

  // Device class (handheld vs computer). Distinct from viewport width — see DeviceService.
  private readonly deviceService = inject(DeviceService);

  /**
   * Hide the top and left navigation and give the route the whole viewport.
   *
   * Resolved from the URL synchronously here, for the same reason `showNavigation` is: if it
   * started false, a direct load of a full-screen route on a phone would render the header and
   * sidebar for one frame before removing them, and the operator would watch the screen
   * rearrange itself while the camera was starting.
   *
   * Only ever true on a handheld. On a laptop the menu is the way out of a page, and a browser
   * window narrowed to phone width is not a reason to take it away.
   */
  isFullScreenRoute = this.deviceService.isHandheld && isFullScreenPath(window.location.pathname);

  constructor(
    private titleService: Title,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private translationService: TranslationService,
    private readOnlyMode: ReadOnlyModeService,
  ) {}

  async ngOnInit() {
    // Set the document title based on environment
    const environmentSuffix =
      environment.production || !environment.environmentName
        ? ''
        : ` ${environment.environmentName.toUpperCase()}`;
    this.titleService.setTitle(`Cargo Cockpit${environmentSuffix}`);

    // Re-evaluate showNavigation after APP_INITIALIZER completes.
    // During OAuth callback, showNavigation was set to false (code= in URL).
    // After APP_INITIALIZER completes OAuth exchange, the code= is cleared,
    // so we need to re-check visibility here.
    if (!this.showNavigation && !window.location.search.includes('code=')) {
      this.updateNavigationVisibility(window.location.pathname);
    }

    // Listen to route changes to control navigation visibility.
    // The initial value of showNavigation is already set from window.location.pathname
    // in the field initializer, so we don't override it with this.router.url here
    // (the router may not have completed initial navigation yet).
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async (event: NavigationEnd) => {
        this.updateNavigationVisibility(event.url);
        // After OAuth callback completes and navigates, load user info
        // Verify authentication to prevent API calls during redirect to login
        if (this.showNavigation && !this.userName) {
          const isAuthenticated = await this.authService.isAuthenticated();
          if (isAuthenticated) {
            this.loadUserInformation();
          }
        }
      });

    // Only load user data on authenticated pages (not login page).
    // Loading user info on the login page triggers API calls that fail,
    // causing the auth interceptor to retry tokens and redirect.
    // Also verify user is actually authenticated before making API calls
    // to prevent race condition with redirect to login.
    if (this.showNavigation) {
      const isAuthenticated = await this.authService.isAuthenticated();
      if (isAuthenticated) {
        await this.loadUserInformation();
      }
    }
  }

  private async loadUserInformation() {
    try {
      // Get user information from Cognito token
      const [firstName, lastName, email, fullName] = await Promise.all([
        this.authService.getUserFirstName().catch(() => ''),
        this.authService.getUserLastName().catch(() => ''),
        this.authService.getUserEmail().catch(() => ''),
        this.authService.getUserCommonName().catch(() => ''),
      ]);

      // Determine best display name
      const displayName = fullName || `${firstName} ${lastName}`.trim() || email || 'User';

      // Set user name and avatar
      this.userName = displayName;
      this.userAvatar = this.generateAvatar(displayName);

      // Ensure user exists in database and get full profile
      this.userService.ensureUserExists().subscribe({
        next: () => {
          // Load the user profile
          this.loadUserProfile();
        },
        error: (error) => {
          console.error('Error ensuring user exists:', error);
          // Still try to load profile if user already exists
          this.loadUserProfile();
        },
      });
    } catch (error) {
      console.error('Error loading user information:', error);
      this.userName = 'User';
      this.userAvatar = 'U';
    }
  }

  private loadUserProfile() {
    this.userService.getCurrentUser().subscribe({
      next: (profile) => {
        this.userProfile = profile;
        // Resolve read-only mode here too: app initialization can time out
        // before it sees the profile, and this runs on every session
        this.readOnlyMode.applyProfile(profile);
        // Update display name with profile information if available
        if (profile.firstName && profile.lastName) {
          this.userName = `${profile.firstName} ${profile.lastName}`;
          this.userAvatar = this.generateAvatar(this.userName);
        }
        // Initialize UI language from user profile preference
        this.translationService.initializeFromUserProfile(profile.language);
      },
      error: (error) => {
        console.error('Error loading user profile:', error);
      },
    });
  }

  private generateAvatar(name: string): string {
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    } else if (nameParts.length === 1 && nameParts[0].length > 0) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  private updateNavigationVisibility(url: string) {
    this.showNavigation = !url.includes('/inactive-user') && !url.includes('/login');
    this.isFullScreenRoute = this.deviceService.isHandheld && isFullScreenPath(url);

    // Reached by tapping a link in the mobile drawer, which leaves the drawer flagged open.
    // It is not rendered while full screen, so it cannot be dismissed — and it would still be
    // open on whatever screen the operator went to next.
    if (this.isFullScreenRoute) {
      this.mobileMenuOpen.set(false);
    }
  }

  onLeftNavToggle(collapsed: boolean) {
    this.isLeftNavCollapsed = collapsed;
  }

  /**
   * Close mobile menu when backdrop is clicked
   */
  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  /**
   * Handle mobile menu state change from left-navigation (e.g., close button click)
   */
  onMobileMenuStateChange(isOpen: boolean): void {
    this.mobileMenuOpen.set(isOpen);
  }
}
